import { ProductOption, ResyncResult } from "../../datastore/ShippingTimelineDataStore";

/** Typed, ready-to-render values for the success feedback panel. */
export interface ResyncResultDisplayValues {
    ordersRecomputedText: string;
    lineItemsRecomputedText: string;
    /** Null when nothing was backfilled — the view hides the row entirely. */
    backfilledText: string | null;
    elapsedText: string;
}

/**
 * Pure display converter (no React) for the Resync Shipping Timelines page.
 * Turns raw models into strings the view renders without further computation.
 */
export class ShippingTimelineResyncDisplayConverter {
    /** Human sentence describing what the current selection will recompute. */
    public describeScope(selectedProducts: ProductOption[]): string {
        if (selectedProducts.length === 0) {
            return "No products currently selected. This will recompute ALL order line items that are still pending fulfillment.";
        }
        const noun = selectedProducts.length === 1 ? "product" : "products";
        return `This will recompute pending-fulfillment order line items for variants of the ${selectedProducts.length} selected ${noun} only.`;
    }

    public toResultDisplayValues(result: ResyncResult): ResyncResultDisplayValues {
        return {
            ordersRecomputedText: `${result.recomputedOrderCount.toLocaleString()} ${this.pluralize(result.recomputedOrderCount, "order")}`,
            lineItemsRecomputedText: `${result.recomputedLineItemCount.toLocaleString()} ${this.pluralize(result.recomputedLineItemCount, "line item")}`,
            backfilledText: result.backfilledCount > 0
                ? `${result.backfilledCount.toLocaleString()} missing ${this.pluralize(result.backfilledCount, "timeline")} backfilled`
                : null,
            elapsedText: this.formatElapsed(result.elapsedMs),
        };
    }

    private formatElapsed(elapsedMs: number): string {
        if (elapsedMs < 1000) {
            return `${elapsedMs} ms`;
        }
        const seconds = elapsedMs / 1000;
        if (seconds < 60) {
            return `${seconds.toFixed(1)} s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainderSeconds = Math.round(seconds % 60);
        return `${minutes} min ${remainderSeconds} s`;
    }

    private pluralize(count: number, singular: string): string {
        return count === 1 ? singular : `${singular}s`;
    }
}

export const shippingTimelineResyncDisplayConverter = new ShippingTimelineResyncDisplayConverter();
